/**
 * Legal i18n — framework-agnostic. Owns the `legal.*` namespace catalogs
 * (8 mandatory locales: ro, en, bg, de, el, fr, hu, it). Apps don't need
 * next-intl or Lingui to render legal pages — they just pass `locale`.
 *
 * Keys lazy-load on demand so the initial bundle ships only EN + RO
 * (canonical pair). Other locales are dynamically imported the first time
 * `getLegalT(locale)` runs for them.
 */

import enMessages from "./messages/legal-en.json";
import roMessages from "./messages/legal-ro.json";

export type LegalCatalog = Record<string, unknown>;

const CATALOGS: Record<string, LegalCatalog> = {
  en: enMessages as LegalCatalog,
  ro: roMessages as LegalCatalog,
};

/**
 * Locales we serve translated legal content for. Anything outside this list
 * falls back to English (or whatever has been preloaded if EN missing).
 */
export const LEGAL_LOCALES = ["ro", "en", "bg", "de", "el", "fr", "hu", "it"] as const;

export type LegalLocale = (typeof LEGAL_LOCALES)[number];

export function isLegalLocale(value: string): value is LegalLocale {
  return (LEGAL_LOCALES as readonly string[]).includes(value);
}

/**
 * Resolve a dotted key (`terms.s1p1`) against the catalog. Walks nested
 * objects, returns the leaf string. Missing key → returns the key itself
 * (so the UI degrades to the English raw-key string instead of "undefined"
 * or empty).
 */
function resolveKey(catalog: LegalCatalog, key: string): string {
  const segments = key.split(".");
  let cursor: unknown = catalog;
  for (const seg of segments) {
    if (cursor && typeof cursor === "object" && seg in cursor) {
      cursor = (cursor as Record<string, unknown>)[seg];
    } else {
      return key;
    }
  }
  return typeof cursor === "string" ? cursor : key;
}

/**
 * Synchronous "is this locale's catalog already in memory?" check.
 *
 * EN + RO are statically imported above, so they answer `true` on the very
 * first render in every environment (server render included) — the two live
 * markets never wait. Everything else answers `false` until
 * `loadLegalLocale()` has resolved. Client hosts use it to seed their
 * "catalog ready" state so RO/EN never flash a placeholder.
 *
 * Note for RSC hosts: the server-component graph and the client-component
 * SSR graph get separate module instances, so a locale awaited in a server
 * component is NOT visible here during that component's client-side SSR
 * pass. Never gate server-rendered legal TEXT on this — server components
 * should `await loadLegalLocale()` and render the text directly.
 */
export function isLegalLocaleLoaded(locale: string): boolean {
  return CATALOGS[locale] !== undefined;
}

/**
 * Pre-load a locale's catalog. Called by hosts (apps/web layout, apps/app
 * route loader) before mounting the shell to avoid a first-render flicker
 * with raw keys. Idempotent. Returns the catalog so callers can hold a
 * reference if useful.
 */
export async function loadLegalLocale(locale: string): Promise<LegalCatalog> {
  if (CATALOGS[locale]) return CATALOGS[locale];
  if (!isLegalLocale(locale)) return CATALOGS.en;
  // Vite + tsc both follow this dynamic import pattern; bundlers code-split
  // by locale automatically. Both apps benefit from the chunk.
  const mod = await import(`./messages/legal-${locale}.json`);
  CATALOGS[locale] = mod.default as LegalCatalog;
  return CATALOGS[locale];
}

/**
 * Build a `t(key, vars?)` function bound to a locale. Supports `{name}`-style
 * placeholder substitution so callers can render strings like
 * `countrySection.title` ("Country-specific provisions for {country}") without
 * a separate ICU pass.
 *
 * Falls back to EN when the locale's catalog isn't preloaded; call
 * `loadLegalLocale(locale)` BEFORE rendering for non-EN/RO locales to avoid
 * a first-paint flicker with English content.
 */
export type LegalTFn = (key: string, vars?: Record<string, string | number>) => string;

export function getLegalT(locale: string): LegalTFn {
  return createLegalT(CATALOGS[locale] ?? CATALOGS.en);
}

/**
 * Same translator, bound to an arbitrary message object instead of a whole
 * locale catalog. Lets a server component resolve a subtree with
 * `getLegalSubtree()` and hand it to a client component as a plain prop — the
 * client then renders the right language on its FIRST paint, with no catalog
 * fetch and no English-then-swap flash.
 */
export function createLegalT(messages: LegalCatalog): LegalTFn {
  return (key, vars) => {
    let result = resolveKey(messages, key);
    if (vars) {
      for (const [name, value] of Object.entries(vars)) {
        result = result.replaceAll(`{${name}}`, String(value));
      }
    }
    return result;
  };
}

/**
 * Pluck one nested branch of a locale's catalog (e.g. `"reportContent"`).
 * Returns `{}` when the branch is missing so callers degrade to raw keys
 * rather than crashing. `await loadLegalLocale(locale)` first — this is a
 * synchronous read of whatever is in memory.
 */
export function getLegalSubtree(locale: string, path: string): LegalCatalog {
  const catalog = CATALOGS[locale] ?? CATALOGS.en;
  let cursor: unknown = catalog;
  for (const seg of path.split(".")) {
    if (cursor && typeof cursor === "object" && seg in cursor) {
      cursor = (cursor as Record<string, unknown>)[seg];
    } else {
      return {};
    }
  }
  return cursor && typeof cursor === "object" ? (cursor as LegalCatalog) : {};
}

/** Pick the right LocalizedString variant for the legal block. */
export function tLocal(obj: Record<string, string> | undefined | null, locale: string): string {
  if (!obj) return "";
  return obj[locale] || obj.en || obj.ro || Object.values(obj)[0] || "";
}
